import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm'; // Add this import
import { useChat } from '../../../hooks/useChat';
import ChatChart from '../../ChatChart';
import CompanyReportDisplay from '../CompanyReportDisplay';
import './ChatInterface.css';

const ChatInterface = () => {
  const [inputValue, setInputValue] = useState('');
  const [selectedClient, setSelectedClient] = useState('openai');
  const messagesEndRef = useRef(null);
  
  // Use the custom chat hook
  const {
    messages,
    isLoading,
    error,
    availableClients,
    currentClient,
    sendMessage,
    clearChat,
    switchClient,
    testChat,
    loadAvailableClients,
    getChatStats
  } = useChat(selectedClient);

  // Load available clients on component mount
  useEffect(() => {
    loadAvailableClients();
  }, [loadAvailableClients]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    try {
      await sendMessage(inputValue);
      setInputValue('');
    } catch (err) {
      console.error('Failed to send message:', err);
    }
  };

  const handleClearChat = () => {
    clearChat();
  };

  const handleClientSwitch = (newClient) => {
    setSelectedClient(newClient);
    switchClient(newClient);
  };

  const handleTestChat = async () => {
    try {
      await testChat();
    } catch (err) {
      console.error('Test failed:', err);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Use useLayoutEffect for immediate DOM updates (before browser paint)
  useLayoutEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatMessage = (message) => {
    if (message.isError) {
      return (
        <div className="message-content error">
          <span className="error-icon">⚠️</span>
          {message.content}
        </div>
      );
    }
    
    // Check if this message contains company report search results
    let hasCompanyReports = false;
    let companyReportData = null;
    
    try {
      // Try to parse the content as JSON to check for company report results
      const parsedContent = JSON.parse(message.content);
      if (parsedContent.success && 
          parsedContent.report_generated_succesfully_by_report_tool) {
        hasCompanyReports = true;
        companyReportData = parsedContent;
      }
    } catch (error) {
      console.error('Failed to parse company report data:', error);
    }
    
    // Check if this message contains a chart
    let hasChart = false;
    try {
      const chartData = JSON.parse(message.content);
      hasChart = chartData.chart_generated_succesfully_by_chart_tool && chartData.chart_data?.image_base64;
    } catch (error) {
      hasChart = false;
    }

    return (
      <div className="message-content">
        {/* Show company report results if present */}
        {hasCompanyReports && companyReportData ? (
          <CompanyReportDisplay 
            results={companyReportData.results}
            query={companyReportData.query}
            searchType={companyReportData.search_type}
          />
        ) : (
          /* Only show text content if there's no company reports and no chart */
          !hasChart && (
            <ReactMarkdown 
              remarkPlugins={[remarkGfm]}
              components={{
                // Custom styling for different markdown elements
                h1: ({node, ...props}) => <h1 style={{fontSize: '1.5em', margin: '0.5em 0'}} {...props} />,
                h2: ({node, ...props}) => <h2 style={{fontSize: '1.3em', margin: '0.4em 0'}} {...props} />,
                h3: ({node, ...props}) => <h3 style={{fontSize: '1.1em', margin: '0.3em 0'}} {...props} />,
                p: ({node, ...props}) => <p style={{margin: '0.5em 0'}} {...props} />,
                ul: ({node, ...props}) => <ul style={{margin: '0.5em 0', paddingLeft: '1.5em'}} {...props} />,
                ol: ({node, ...props}) => <ol style={{margin: '0.5em 0', paddingLeft: '1.5em'}} {...props} />,
                li: ({node, ...props}) => <li style={{margin: '0.2em 0'}} {...props} />,
                strong: ({node, ...props}) => <strong style={{fontWeight: 'bold'}} {...props} />,
                em: ({node, ...props}) => <em style={{fontStyle: 'italic'}} {...props} />,
                code: ({node, inline, ...props}) => 
                  inline ? 
                    <code style={{
                      backgroundColor: '#f4f4f4', 
                      padding: '0.2em 0.4em', 
                      borderRadius: '3px',
                      fontFamily: 'monospace',
                      fontSize: '0.9em'
                    }} {...props} /> :
                    <pre style={{
                      backgroundColor: '#f4f4f4',
                      padding: '1em',
                      borderRadius: '5px',
                      overflow: 'auto',
                      margin: '0.5em 0'
                    }}><code {...props} /></pre>,
                blockquote: ({node, ...props}) => (
                  <blockquote style={{
                    borderLeft: '4px solid #ddd',
                    margin: '0.5em 0',
                    paddingLeft: '1em',
                    fontStyle: 'italic',
                    color: '#666'
                  }} {...props} />
                ),
                table: ({node, ...props}) => (
                  <div style={{
                    overflow: 'auto', 
                    margin: '0.5em 0',
                    borderRadius: '4px',
                    border: '1px solid #e0e0e0'
                  }}>
                    <table style={{
                      borderCollapse: 'collapse',
                      width: '100%',
                      fontSize: '0.9em',
                      backgroundColor: '#fff'
                    }} {...props} />
                  </div>
                ),
                th: ({node, ...props}) => (
                  <th style={{
                    border: '1px solid #e0e0e0',
                    padding: '8px 12px',
                    backgroundColor: '#f8f9fa',
                    fontWeight: '600',
                    textAlign: 'left',
                    fontSize: '0.9em'
                  }} {...props} />
                ),
                td: ({node, ...props}) => (
                  <td style={{
                    border: '1px solid #e0e0e0',
                    padding: '8px 12px',
                    fontSize: '0.9em',
                    verticalAlign: 'top'
                  }} {...props} />
                ),
                tr: ({node, ...props}) => (
                  <tr style={{
                    borderBottom: '1px solid #e0e0e0'
                  }} {...props} />
                )
              }}
            >
              {message.content}
            </ReactMarkdown>
          )
        )}
        
        {/* Display charts if present - NEW STRUCTURE */}
        {console.log('ChatInterface: Checking for charts in message:', {
          chartGeneratedSuccessfully: !!message.chart_generated_succesfully_by_chart_tool,
          hasChartData: !!message.chart_data,
          hasImageBase64: !!message.chart_data?.image_base64,
          messageKeys: Object.keys(message),
          chartData: message.chart_data
        })}
        
        {/* Chart display using parsed JSON content */}
        {(() => {
          try {
             
            const chartData = JSON.parse(message.content);
            
            if (chartData.chart_generated_succesfully_by_chart_tool && chartData.chart_data?.image_base64) {
              console.log('ChatInterface: ✅ Chart found and rendering:', chartData);
                return (
                  <div className="message-charts">
                    <div className="chart-container" style={{ 
                      marginTop: '16px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}>
                      <img 
                        src={`data:image/png;base64,${chartData.chart_data.image_base64}`}
                        alt="Stock chart"
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: '70vh',
                          width: 'auto',
                          height: 'auto',
                          borderRadius: '4px',
                          border: '1px solid #e0e0e0',
                          objectFit: 'contain'
                        }}
                      />
                    </div>
                </div>
              );
            }
            return null;
          } catch (error) {
            console.error('ChatInterface: Failed to parse chart content:', error);
            return null;
          }
        })()}
      </div>
    );
  };

  const chatStats = getChatStats();

  return (
    <div className="chat-interface">
      <div className="chat-header">
        <h2>AI Financial Assistant</h2>
        <p>Ask me about stocks, analysis, or investment advice</p>
      </div>
      
      <div className="chat-messages">
        {messages.length === 0 && (
          <div className="welcome-message">
            <div className="message bot">
              <div className="message-content">
                <ReactMarkdown>
                  {`# Hello! 👋

I'm your **AI Financial Assistant** powered by Viral Trade. 

## What I can help you with:
- 📊 **Stock Analysis** - Technical and fundamental analysis
- 💰 **Investment Advice** - Portfolio recommendations
- 📈 **Market Insights** - Trends and predictions
- 📋 **Financial Reports** - Earnings and performance data

*Ask me anything about stocks, investments, or market analysis!*`}
                </ReactMarkdown>
              </div>
            </div>
          </div>
        )}
        
        {messages.map((message) => {
          console.log('ChatInterface: 🔍 Processing message:', {
            id: message.id,
            role: message.role,
            content: message.content,
            charts: message.charts,
            chart_data: message.chart_data,
            chart_generated_succesfully_by_chart_tool: message.chart_generated_succesfully_by_chart_tool,
            allKeys: Object.keys(message)
          });
          
          return (
            <div key={message.id} className={`message ${message.role}`}>
              {formatMessage(message)}
            </div>
          );
        })}
        
        {isLoading && (
          <div className="message assistant">
            <div className="message-content typing">
              <span>AI is thinking</span>
              <span className="dots">...</span>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>
      
      <div className="chat-controls">
        <button 
          onClick={handleClearChat}
          disabled={isLoading || messages.length === 0}
          className="clear-button"
        >
          🗑️ Clear Chat
        </button>
        
        {error && (
          <div className="error-message">
            <span>Error: {error}</span>
          </div>
        )}
      </div>
      
      <div className="chat-input">
        <textarea
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Ask me about stocks, analysis, or investment advice..."
          disabled={isLoading}
          rows={1}
          className="message-input"
        />
        <button 
          onClick={handleSendMessage}
          disabled={!inputValue.trim() || isLoading}
          className="send-button"
        >
          {isLoading ? '🔄' : '📤'}
        </button>
      </div>
    </div>
  );
};

export default ChatInterface; 